"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import { ExternalLink, Figma } from "lucide-react";
import { CaseStudy } from "@/types";

interface CaseStudiesSectionProps {
    caseStudies: CaseStudy[];
}

export default function CaseStudiesSection({ caseStudies }: CaseStudiesSectionProps) {
    return (
        <section
            id="case-studies"
            className="section-snap flex items-center bg-surface min-h-screen py-20"
        >
            <div className="container mx-auto px-section-x">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="mb-16 text-center"
                >
                    <h2
                        className="text-section-title font-bold text-foreground leading-tight mb-4"
                        style={{ fontFamily: "var(--font-playfair), Georgia, serif" }}
                    >
                        UI/UX Case Studies
                    </h2>
                    <p className="text-text-secondary max-w-2xl mx-auto">
                        Exploring design solutions and user experiences
                    </p>
                </motion.div>

                {/* Case Studies Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
                    {caseStudies.map((study, index) => (
                        <CaseStudyCard key={study.id} study={study} index={index} />
                    ))}
                </div>
            </div>
        </section>
    );
}

function CaseStudyCard({ study, index }: { study: CaseStudy; index: number }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="group bg-surface-elevated rounded-2xl overflow-hidden shadow-sm hover:shadow-xl active:shadow-xl transition-all duration-300 border border-border flex flex-col h-full"
        >
            {/* Thumbnail */}
            <div className="relative aspect-[16/9] overflow-hidden bg-surface">
                <Image
                    src={study.thumbnail}
                    alt={study.title}
                    fill
                    className="object-cover transition-transform duration-500 group-hover:scale-105 group-active:scale-105"
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 600px"
                />
            </div>

            {/* Content */}
            <div className="p-8 flex flex-col flex-grow">
                <h3 className="text-xl font-bold text-foreground mb-1 group-hover:text-accent group-active:text-accent transition-colors">
                    {study.title}
                </h3>
                <p className="text-sm font-medium text-text-secondary mb-4 uppercase tracking-wide">
                    {study.subtitle}
                </p>
                <p className="text-text-secondary leading-relaxed mb-6 flex-grow">
                    {study.description}
                </p>

                {/* Action Buttons */}
                <div className="flex flex-wrap items-center gap-3 mt-auto">
                    {study.links.figma && (
                        <a
                            href={study.links.figma}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 border bg-foreground text-background hover:bg-accent active:bg-accent hover:text-white active:text-white border-transparent"
                        >
                            <Figma className="w-4 h-4" />
                            View Figma
                        </a>
                    )}

                    {study.links.live && (
                        <a
                            href={study.links.live}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 border bg-background text-foreground border-border hover:border-accent active:border-accent"
                        >
                            <ExternalLink className="w-4 h-4" />
                            Live Website
                        </a>
                    )}
                </div>
            </div>
        </motion.div>
    );
}
